import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ActionDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { ReportSchedulingView } from '@futrli/views/ReportSchedulingView';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { ScheduleListItem } from '@futrli/views/processors/reportScheduling/reportSchedulingList';

import { useRouteIds } from '../../../hooks/useRouteIds';

interface ApproveScheduleDialogContainerProps {
  isOpen: boolean;
  onClose: () => void;
  item: ScheduleListItem;
}

export const ApproveScheduleDialogContainer = ({ isOpen, onClose, item }: ApproveScheduleDialogContainerProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(
      () =>
        new ReportSchedulingView(workspaceId, organisationId, isConsolidation, {
          schedules: false,
          awaitingReview: false,
          exported: false
        }),
      [workspaceId, organisationId, isConsolidation]
    )
  );

  const { screen, handleAction: handleConfirm, handleClose } = useDialogWizardState({
    isLoading: view.state.isApprovingDispatched,
    error: view.state.errorApprovingDispatched,
    onAction: () => view.approveDispatched({ dispatchedReportId: item.sourceItem.id }),
    onClose
  });

  const title = intl.formatMessage({ id: 'reporting.approve_and_send', defaultMessage: 'Approve and send' });

  const confirmDescription = intl.formatMessage({
    id: 'reporting.approve_confirm_description',
    defaultMessage: 'Clicking confirm will schedule the report to be sent.'
  });

  const loadingMessage = intl.formatMessage({
    id: 'reporting.approving',
    defaultMessage: 'Approving...'
  });

  const successMessage = intl.formatMessage({
    id: 'reporting.report_approved_sent',
    defaultMessage: 'Report approved and sent'
  });

  const errorMessage = view.state.errorApprovingDispatched?.message;

  return (
    <ActionDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      screen={screen}
      title={title}
      subtitle={item.name}
      confirmTitle={intl.formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' })}
      confirmDescription={confirmDescription}
      loadingMessage={loadingMessage}
      successMessage={successMessage}
      errorMessage={errorMessage}
      dataTestId="approve-schedule-dialog"
    />
  );
};
